// React Dependencies
import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select } from '@material-ui/core';
import { Close as CloseIcon, InfoOutlined as InfoIcon } from '@material-ui/icons';
import { useSelector } from 'react-redux';

// Internal Dependencies
import MText from '../../../../components/Common/MText';
import OverLoading from '../../../../components/Loading';
import Colours from '../../../../styles/colours';
import MButton from '../../../../components/MButton';
import ApiService from '../../../../services/apiService';
import MDropzone from '../../../../components/Common/form/MDropzone';
import Constants, { SamplePromoCodeRedemptionCsvUrl, Status } from '../../../../common/constants';
import { IsCsvFile, IsEmpty, parseCsvFile } from '../../../../common/util';

const PreRedeemStatus = {
  Redeemable: 'Redeemable',
  Skipping:   'Skipping',
};

const PostRedeemStatus = {
  Redeemable: 'Redeemed',
  Skipping:   'Skipped',
};

const promoCodeRedeemSampleImg = require('../../../../assets/images/promo-code-redeem-sample.png');

const BulkRedeemModal = (props) => {
  const pageId = useSelector((state) => state.page.selected.id);
  const [isLoading, setIsLoading] = useState(false);
  const [outletId, setOutletId] = useState(null);
  const [redemptionData, setRedemptionData] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const redeemableCount = useMemo(() => (
    _.filter(redemptionData, (d) => d.redeemable).length
  ), [redemptionData]);

  const RedeemStatus = useMemo(() => (
    isSubmitted ? PostRedeemStatus : PreRedeemStatus
  ), [isSubmitted]);

  const columns = useMemo(() => [
    { title: 'Outlet', field: 'pageName', editable: 'never', sorting: true },
    { title: 'Code', field: 'code', editable: 'never', sorting: true },
    { title: 'Redemption Count', field: 'redemptionCount', editable: 'never', sorting: true, type: 'numeric' },
    { title: 'Discount Code Amount', field: 'discountCodeAmount', editable: 'never', sorting: true },
    { title: 'Total Amount', field: 'totalAmount', editable: 'never', sorting: true },
    {
      title:     'Redeem Status',
      field:     'redeemStatus',
      editable:  'never',
      sorting:   true,
      render:    (row) => (isSubmitted ? PostRedeemStatus[row.redeemStatus] : row.redeemStatus),
      cellStyle: (_value, row) => ({
        color: row.redeemable ? Colours.Green : Colours.Yellow,
      }),
      filterComponent: (filterProps) => (
        <Select
          className="w-100"
          onChange={(e) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={PreRedeemStatus.Redeemable}>{RedeemStatus.Redeemable}</MenuItem>
          <MenuItem value={PreRedeemStatus.Skipping}>{RedeemStatus.Skipping}</MenuItem>
        </Select>
      ),
    },
    { title: 'Remark', field: 'remark', editable: 'never', sorting: true },
  ], [isSubmitted]);

  const isSubmitDisabled = isLoading || IsEmpty(outletId) || redeemableCount < 1;
  const tableShown = !IsEmpty(redemptionData);
  const uploadInputShown = !IsEmpty(outletId) && !tableShown;

  const handleSubmitClick = async () => {
    try {
      if (redeemableCount < 1) {
        return;
      }
      setIsLoading(true);

      const ids = _.chain(redemptionData)
        .filter((d) => d.redeemable && !IsEmpty(d.promoCodeId))
        .map((d) => d.promoCodeId)
        .value();

      await ApiService.bulkUpdatePromoCodes(pageId, { ids, status: Status.Redeemed });
      setIsSubmitted(true);
      props.onActionCompleted();

      toast.success(`Redeemed ${redeemableCount} promo code(s)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const validateRedemptionData = async (formattedParsedData) => {
    formattedParsedData = _.map(
      formattedParsedData,
      (item) => _.pick(item, ['code', 'pageName', 'redemptionCount', 'discountCodeAmount', 'totalAmount']),
    );

    const redeemCheckResult = await ApiService.checkPromoCodesRedeemable(
      pageId,
      outletId,
      { promoCodes: formattedParsedData },
    );

    const validatedRedemptionData = _.map(
      formattedParsedData,
      (d, i) => ({
        ...d,
        ...redeemCheckResult[i],
        redeemStatus: redeemCheckResult[i].redeemable
          ? RedeemStatus.Redeemable
          : RedeemStatus.Skipping,
      }),
    );

    return validatedRedemptionData;
  };

  const handleFileChange = async ([file]) => {
    try {
      setIsLoading(true);

      if (IsEmpty(file)) {
        return;
      }

      if (!IsCsvFile(file)) {
        throw new Error('Invalid CSV file.');
      }

      const parsedData = await parseCsvFile(file);
      const formattedParsedData = _.chain(parsedData)
        .filter((item) => !IsEmpty(item['Promo Code']))
        .map((item) => ({
          code:               item['Promo Code'],
          pageName:           item.Outlet,
          redemptionCount:    item['Redemption Count'],
          discountCodeAmount: item['Discount Code Amount'],
          totalAmount:        item['Total Amount'],
        }))
        .value();

      if (IsEmpty(formattedParsedData)) {
        throw new Error('Invalid promo code redemption format.');
      }

      const validatedRedemptionData = await validateRedemptionData(formattedParsedData);
      setRedemptionData(validatedRedemptionData);
    } catch (err) {
      toast.error(err.message);
      setRedemptionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const revalidateRedemptionData = async () => {
    try {
      if (IsEmpty(outletId) || IsEmpty(redemptionData)) {
        return;
      }
      setIsLoading(true);
      const validatedRedemptionData = await validateRedemptionData(redemptionData);
      setRedemptionData(validatedRedemptionData);
    } catch (err) {
      toast.error(err.message);
      setRedemptionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearTableData = () => {
    setRedemptionData([]);
    setIsSubmitted(false);
  };

  useEffect(() => {
    if (!props.open) {
      setOutletId(null);
      setRedemptionData([]);
      setIsSubmitted(false);
    }
  }, [props.open]);

  useEffect(() => {
    setIsSubmitted(false);
    revalidateRedemptionData();
  }, [outletId]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth={tableShown ? 'lg' : 'sm'}
    >
      {isLoading && <OverLoading />}
      <DialogTitle className="pt-3">
        <div className="d-flex justify-content-between align-items-center">
          <MText
            bold
            variant="h2"
            size={16}
            text="Bulk Redeem Promo Codes"
          />
          <IconButton size="medium" onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="pb-4">
        <div className="d-flex flex-column" style={{ gap: 3 }}>
          <MText
            size={11}
            colour={Colours.Gray}
            text="1) Select outlet"
          />
          <Select
            value={outletId}
            variant="outlined"
            style={{ height: 40, maxWidth: 550 }}
            onChange={(e) => setOutletId(e.target.value)}
            disabled={!IsEmpty(redemptionData)}
          >
            {
              IsEmpty(props.outlets)
                ? (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )
                : _.map(props.outlets, (outlet) => (
                  <MenuItem
                    key={outlet.id}
                    style={{ fontSize: 12 }}
                    value={outlet.id}
                  >
                    {outlet.name}
                  </MenuItem>
                ))
            }
          </Select>
        </div>

        {
          uploadInputShown && (
            <div className="d-flex flex-column mt-4" style={{ gap: 5 }}>
              <MText
                size={11}
                colour={Colours.Gray}
                text="2) Upload Promo Code redemption file"
              />
              <div
                style={{ backgroundColor: Colours.LightBlue3, gap: 8 }}
                className="p-2 d-flex align-items-center rounded-lg"
              >
                <InfoIcon fontSize="13px" htmlColor={Colours.Blue1} />
                <MText
                  medium
                  size={12}
                  colour={Colours.Blue2}
                >
                  To bulk redeem, upload a .csv file following the format in this
                  {' '}
                  <a
                    href={SamplePromoCodeRedemptionCsvUrl}
                    download="Sample Redeem File.csv"
                    style={{ color: Colours.Blue, fontWeight: 'bold' }}
                  >
                    file
                  </a>
                  .
                </MText>
              </div>

              <div className="w-100 d-flex flex-column align-items-center my-2">
                <MText text="Sample file data" />
                <img
                  src={promoCodeRedeemSampleImg}
                  alt="sample promo codes"
                  draggable={false}
                  className="w-100"
                  style={{ userSelect: 'none' }}
                />
              </div>

              <MDropzone
                maxFiles={1}
                maxFileSize={Constants.BinaryOneMBSize * 3}
                acceptedFiles={['text/csv']}
                displayText="Click or drag a CSV file here to upload"
                onChange={handleFileChange}
              />
            </div>
          )
        }

        {
          tableShown && (
            <div className="w-100 d-flex flex-column mt-3" style={{ gap: 3 }}>
              <MText
                size={11}
                colour={Colours.Gray}
                text="Redemption Details"
              />
              <MaterialTable
                columns={columns}
                data={redemptionData}
                isLoading={isLoading}
                options={{
                  filtering:           true,
                  toolbar:             false,
                  emptyRowsWhenPaging: false,
                  pageSize:            25,
                  pageSizeOptions:     [5, 10, 25, 50, 100],
                  debounceInterval:    250,
                }}
              />
            </div>
          )
        }
      </DialogContent>

      <DialogActions className="pb-4 px-4 justify-content-between">
        <div className="d-flex flex-column">
          {
            tableShown && (
              <>
                <MText
                  size={11}
                  colour={Colours.Gray}
                  text={`${RedeemStatus.Redeemable}: ${redeemableCount}`}
                />
                <MText
                  size={11}
                  colour={Colours.Gray}
                  text={`${RedeemStatus.Skipping}: ${redemptionData.length - redeemableCount}`}
                />
              </>
            )
          }
        </div>

        <div className="d-flex justify-content-between" style={{ gap: 5 }}>
          {
            tableShown && (
              <MButton
                className="mr-3"
                variant="contained"
                colour={Colours.LightGray5}
                onClick={clearTableData}
                text="Back to upload"
              />
            )
          }
          {
            isSubmitted
              ? (
                <MButton
                  variant="contained"
                  colour={Colours.Secondary}
                  textColour={Colours.White}
                  onClick={props.onClose}
                  text="Done"
                />
              )
              : (!IsEmpty(outletId) && !IsEmpty(redemptionData))
                ? (
                  <MButton
                    className="px-5"
                    variant="contained"
                    colour={isSubmitDisabled ? Colours.LightGray2 : Colours.Secondary}
                    textColour={Colours.White}
                    disabled={isSubmitDisabled}
                    onClick={handleSubmitClick}
                    text={`Redeem${redeemableCount > 0 ? ` (${redeemableCount})` : ''}`}
                  />
                ) : null
          }
        </div>
      </DialogActions>
    </Dialog>
  );
};

BulkRedeemModal.propTypes = {
  open:              PropTypes.bool.isRequired,
  onClose:           PropTypes.func.isRequired,
  outlets:           PropTypes.arrayOf(PropTypes.shape(Object)),
  onActionCompleted: PropTypes.func.isRequired,
};

BulkRedeemModal.defaultProps = {
  outlets: [],
};

export default BulkRedeemModal;
